import AddIcon from "@mui/icons-material/Add";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import RemoveIcon from "@mui/icons-material/Remove";
import { LoadingButton } from "@mui/lab";
import {
	Alert,
	alpha,
	Box,
	Button,
	CircularProgress,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	TextField,
	Typography,
	useTheme
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CommCard from "src/common/CommCard";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import { useDebounce } from "src/hooks/useDebounce";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import CreateVisitor from "../NewVisitorList/CreateVisitor";
import type {
	ICreateVisitorPayload,
	IVisitorListItem,
} from "../type/newVisitorType";
import type { ITicketConfig } from "../type/ticketConfigType";
import VisitorPhoneSearchSection, {
	VisitorFormValues,
} from "./VisitorPhoneSearchSection";

export interface IFairCommon {
	fair_id: number;
	fair_name: string;
	fair_start_date: string;
	fair_end_date: string;
	fair_venue_layout?: string | null;
}

export interface ITicketDateItem {
	value: string;
	label: string;
	day: string;
	weekday: string;
}

export const generateTicketDates = (
	startDateStr?: string | null,
	endDateStr?: string | null
): ITicketDateItem[] => {
	if (!startDateStr || !endDateStr) return [];

	const [sYear, sMonth, sDay] = startDateStr.split("-").map(Number);
	const [eYear, eMonth, eDay] = endDateStr.split("-").map(Number);

	if (!sYear || !sMonth || !sDay || !eYear || !eMonth || !eDay) return [];

	const start = new Date(sYear, sMonth - 1, sDay);
	const end = new Date(eYear, eMonth - 1, eDay);

	if (start > end) return [];

	const MONTH_NAMES = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];
	const WEEKDAYS = [
		"Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
	];

	const dates: ITicketDateItem[] = [];
	const current = new Date(start);
	let dayIndex = 1;

	while (current <= end) {
		const y = current.getFullYear();
		const m = String(current.getMonth() + 1).padStart(2, "0");
		const d = String(current.getDate()).padStart(2, "0");
		const val = `${y}-${m}-${d}`;
		const lbl = `${current.getDate()} ${MONTH_NAMES[current.getMonth()]}, ${y}`;
		const wk = WEEKDAYS[current.getDay()];

		dates.push({
			value: val,
			label: lbl,
			day: `Day ${dayIndex}`,
			weekday: wk,
		});

		current.setDate(current.getDate() + 1);
		dayIndex++;
	}

	return dates;
};

const getDefaultTicketDate = (dates?: ITicketDateItem[]): string => {
	if (!dates || dates.length === 0) return "";
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	const todayStr = `${year}-${month}-${day}`;

	const matched = dates.find((item) => item.value === todayStr);
	return matched ? matched.value : (dates[0]?.value || "");
};

const getGridXs = (total: number) => {
	if (total <= 1) return { xs: 12 };
	if (total === 2) return { xs: 6 };
	if (total === 3) return { xs: 4 };
	if (total === 4) return { xs: 6, sm: 3 };
	return { xs: 6, sm: 4, md: 3 };
};

const PAYMENT_METHODS = [
	{ value: "cash", label: "Cash" },
	{ value: "online", label: "Online" },
];

const PAYMENT_STATUSES = [
	{ value: "paid", label: "Paid" },
	{ value: "unpaid", label: "Unpaid" },
];

const QUICK_QUANTITIES = [2, 3, 4, 5, 6, 7, 8, 9, 10];

const CreateTicketPage: React.FC = () => {
	const theme = useTheme();
	const navigate = useNavigate();

	const [visitorSearch, setVisitorSearch] = useState<string>("");
	const debouncedSearch = useDebounce(visitorSearch, 400);
	const [selectedVisitor, setSelectedVisitor] = useState<IVisitorListItem | null>(null);
	const [openCreateVisitorModal, setOpenCreateVisitorModal] = useState<boolean>(false);
	const [successMsg, setSuccessMsg] = useState<string>("");
	const [apiError, setApiError] = useState<string>("");

	// Fetch fair dates from /api/common/fair-list
	const { data: rawFair, isLoading: isFairLoading } = useApiQuery<IFairCommon | IFairCommon[]>({
		endPoint: "/api/common/fair-list",
		queryKeys: ["CommonFairList"],
	});

	const fairData: IFairCommon | null = rawFair
		? Array.isArray(rawFair)
			? rawFair[0] || null
			: (rawFair as any)?.data && typeof (rawFair as any).data === "object"
				? Array.isArray((rawFair as any).data)
					? (rawFair as any).data[0] || null
					: (rawFair as any).data
				: rawFair
		: null;

	const ticketDates = useMemo(() => {
		return generateTicketDates(fairData?.fair_start_date, fairData?.fair_end_date);
	}, [fairData?.fair_start_date, fairData?.fair_end_date]);

	// Fetch base ticket config
	const { data: rawTicketConfig } = useApiQuery<ITicketConfig | ITicketConfig[]>({
		endPoint: "/api/v1/admin/visitor/ticket-config",
		queryKeys: ["TicketConfig"],
	});

	const ticketConfig: ITicketConfig | null = rawTicketConfig
		? Array.isArray(rawTicketConfig)
			? rawTicketConfig[0] || null
			: rawTicketConfig
		: null;

	const unitPrice = Number(ticketConfig?.price) || 250;

	// (Retained for commented-out Select Visitor section)
	const { data: visitorsData, isLoading: isVisitorsLoading } =
		useGetQueryWithTotal<IVisitorListItem[]>({
			endPoint: "/api/v1/admin/visitor",
			queryKeys: ["Visitors", debouncedSearch],
			params: {
				limit: 25,
				skip: 0,
				...(debouncedSearch.trim()
					? {
						searchQuery: debouncedSearch.trim(),
					}
					: {}),
			},
		});

	const {
		control,
		register,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<VisitorFormValues>({
		defaultValues: {
			visitor_id: null,
			visitor_phone: "+880",
			visitor_name: "",
			visitor_email: "",
			password: "12345678",
			date: "",
			quantity: 1,
			discount: 0,
			payment_method: "cash",
			payment_status: "paid",
		},
	});

	const watchedDate = watch("date") || getDefaultTicketDate(ticketDates);
	const watchedQuantity = watch("quantity") || 1;
	const watchedDiscount = watch("discount") || 0;

	// Automatically synchronize selected date once dynamic fair dates are loaded
	useEffect(() => {
		if (ticketDates.length > 0) {
			const currentDate = watch("date");
			const isCurrentValid = ticketDates.some((item) => item.value === currentDate);
			if (!currentDate || !isCurrentValid) {
				const defaultDate = getDefaultTicketDate(ticketDates);
				if (defaultDate) {
					setValue("date", defaultDate, { shouldValidate: true });
				}
			}
		}
	}, [ticketDates, setValue, watch]);

	const handleDecrementQuantity = () => {
		const current = Math.max(1, Number(watchedQuantity) || 1);
		if (current > 1) {
			setValue("quantity", current - 1, {
				shouldValidate: true,
				shouldDirty: true,
				shouldTouch: true,
			});
		}
	};

	const handleIncrementQuantity = () => {
		const current = Math.max(1, Number(watchedQuantity) || 1);
		setValue("quantity", current + 1, {
			shouldValidate: true,
			shouldDirty: true,
			shouldTouch: true,
		});
	};

	const grossTotal = unitPrice * Math.max(1, Number(watchedQuantity));
	const discountAmount = Math.max(0, Number(watchedDiscount));
	const netPayable = Math.max(0, grossTotal - discountAmount);

	// Mutation to create visitor / ticket via Visitor API (/api/v1/admin/visitor)
	const { mutateAsync: createVisitorMutation, isPending: isSubmitting } =
		useApiMutation<ICreateVisitorPayload, any>({
			method: "POST",
			endPoint: "/api/v1/admin/visitor",
			invalidateQueries: ["Visitors", "AllVisitorTickets", "TicketScans"],
		});

	const onSubmit = async (formData: VisitorFormValues) => {
		setApiError("");
		setSuccessMsg("");

		try {
			const visitorName = (
				formData.visitor_name ||
				selectedVisitor?.visitor_name ||
				""
			).trim();
			const visitorEmail = (
				formData.visitor_email ||
				selectedVisitor?.visitor_email ||
				""
			)
				.trim()
				.toLowerCase();
			const visitorPhone = (
				formData.visitor_phone ||
				selectedVisitor?.visitor_phone ||
				""
			).trim();

			if (!visitorName) {
				setApiError("Visitor name is required.");
				return;
			}
			if (!visitorEmail) {
				setApiError("Visitor email is required.");
				return;
			}
			if (!visitorPhone || visitorPhone.replace(/\D/g, "").length < 6) {
				setApiError("A valid visitor phone number is required.");
				return;
			}

			// Payload structured strictly according to backend createVisitorSchema
			const visitorPayload: ICreateVisitorPayload = {
				name: visitorName,
				email: visitorEmail,
				phone: visitorPhone || undefined,
				password: formData.password ? formData.password.trim() : "12345678",
				ticket: {
					date: formData.date || watchedDate || getDefaultTicketDate(ticketDates),
					quantity: Math.max(1, Number(formData.quantity) || 1),
					payment_method: formData.payment_method || "cash",
					payment_status: formData.payment_status || "paid",
					discount: Math.max(0, Number(formData.discount) || 0),
				},
			};

			// Single API call to visitor endpoint
			const res: any = await createVisitorMutation(visitorPayload);
			const responseData = res?.data || res;
			const ticketNo =
				responseData?.ticket_no ||
				responseData?.ticket?.ticket_no ||
				res?.ticket_no ||
				(Array.isArray(responseData?.tickets) && responseData?.tickets[0]?.ticket_no) ||
				"";

			// Populate created visitor data so the Print/Download Pass button is immediately visible in Visitor Information
			const createdVisitor: IVisitorListItem | any = {
				visitor_id: responseData?.visitor_id || null,
				visitor_no: responseData?.visitor_no || selectedVisitor?.visitor_no || "",
				visitor_name:
					responseData?.visitor_name ||
					responseData?.name ||
					visitorName,
				visitor_email:
					responseData?.visitor_email ||
					responseData?.email ||
					visitorEmail,
				visitor_phone:
					responseData?.visitor_phone ||
					responseData?.phone ||
					visitorPhone,
				visitor_status: "Approved",
				visitor_created_at:
					responseData?.ticket?.date ||
					responseData?.created_at ||
					new Date().toISOString(),
				ticket: responseData?.ticket,
				tickets: responseData?.ticket ? [responseData.ticket] : responseData?.tickets,
			};

			setSelectedVisitor(createdVisitor);
			setValue("visitor_id", createdVisitor.visitor_id, { shouldValidate: true });
			setValue("visitor_phone", createdVisitor.visitor_phone, { shouldValidate: true });
			setValue("visitor_name", createdVisitor.visitor_name, { shouldValidate: true });
			setValue("visitor_email", createdVisitor.visitor_email, { shouldValidate: true });

			setSuccessMsg(
				`Ticket created successfully!${ticketNo ? ` (Ticket No: ${ticketNo})` : ""
				} Net Total: ৳${netPayable.toFixed(2)}`
			);
		} catch (err: any) {
			const errorMsg =
				err?.response?.data?.message ||
				err?.message ||
				"An error occurred while creating ticket/visitor.";
			setApiError(errorMsg);
		}
	};

	const handleVisitorCreatedSuccess = (createdRes: any) => {
		if (createdRes?.data?.visitor_id || createdRes?.visitor_id) {
			const newVis: IVisitorListItem = createdRes?.data || createdRes;
			setSelectedVisitor(newVis);
			setValue("visitor_id", newVis.visitor_id, { shouldValidate: true });
			setValue("visitor_name", newVis.visitor_name || "", {
				shouldValidate: true,
			});
			setValue("visitor_email", newVis.visitor_email || "", {
				shouldValidate: true,
			});
			setValue("visitor_phone", newVis.visitor_phone || "+880", {
				shouldValidate: true,
			});
		}
	};

	return (
		<CommCard
			helmetTitle="BTTF - Create Ticket"
			headerTitle="Create Visitor Ticket"
			showCardHeader
			showButton
			btnText="Ticket List"
			handleBtnForModalFun={() => navigate("/visitor/ticket-list")}
		>
			<Box
				sx={{
					maxWidth: 750,
					margin: "24px auto",
					px: { xs: 2, sm: 3 },
				}}
				component="form"
				onSubmit={handleSubmit(onSubmit)}
			>
				{successMsg && (
					<Alert
						severity="success"
						icon={<CheckCircleTwoToneIcon fontSize="inherit" />}
						onClose={() => setSuccessMsg("")}
						sx={{ mb: 3, borderRadius: 1.5 }}
					>
						{successMsg}
					</Alert>
				)}

				{apiError && (
					<Alert
						severity="error"
						onClose={() => setApiError("")}
						sx={{ mb: 3, borderRadius: 1.5 }}
					>
						{apiError}
					</Alert>
				)}

				<Grid container spacing={2.5}>

					<Grid item xs={12}>
						<VisitorPhoneSearchSection
							control={control}
							register={register}
							errors={errors}
							setValue={setValue}
							watch={watch}
							selectedVisitor={selectedVisitor}
							setSelectedVisitor={setSelectedVisitor}
						/>
					</Grid>



					{/* Visit Date Selection (2026-10-29, 2026-10-30, 2026-10-31) */}
					<Grid item xs={12}>
						<Box sx={{ mb: 0.5 }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									mb: 0.75,
								}}
							>
								<Typography variant="body2" fontWeight={600}>
									Select Visit Date <span style={{ color: "red" }}>*</span>
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Selected:{" "}
									<strong style={{ color: theme.palette.primary.main }}>
										{watchedDate || "None"}
									</strong>
								</Typography>
							</Box>

							{isFairLoading ? (
								<Box sx={{ display: "flex", gap: 1, width: "100%", py: 0.5 }}>
									<Skeleton variant="circular" width="33%" height={38} sx={{ borderRadius: 1.25 }} />
									<Skeleton variant="circular" width="33%" height={38} sx={{ borderRadius: 1.25 }} />
									<Skeleton variant="circular" width="33%" height={38} sx={{ borderRadius: 1.25 }} />
								</Box>
							) : ticketDates.length > 0 ? (
								<Grid container spacing={1}>
									{ticketDates.map((item) => {
										const isSelected = watchedDate === item.value;
										return (
											<Grid item {...getGridXs(ticketDates.length)} key={item.value}>
												<Box
													onClick={() => {
														setValue("date", item.value, {
															shouldValidate: true,
															shouldDirty: true,
														});
													}}
													sx={{
														py: 0.75,
														px: { xs: 0.5, sm: 1 },
														borderRadius: 1.25,
														cursor: "pointer",
														userSelect: "none",
														border: isSelected
															? `1.5px solid ${theme.palette.primary.main}`
															: "1px solid rgba(0, 0, 0, 0.12)",
														bgcolor: isSelected
															? alpha(theme.palette.primary.main, 0.08)
															: "background.paper",
														boxShadow: isSelected
															? `0 1px 4px ${alpha(
																theme.palette.primary.main,
																0.2
															)}`
															: "none",
														transition: "all 0.15s ease-in-out",
														display: "flex",
														flexDirection: { xs: "column", sm: "row" },
														alignItems: "center",
														justifyContent: "center",
														gap: { xs: 0.3, sm: 0.8 },
														minHeight: 38,
														"&:hover": {
															bgcolor: isSelected
																? alpha(theme.palette.primary.main, 0.12)
																: alpha(theme.palette.primary.main, 0.04),
															borderColor: theme.palette.primary.main,
														},
													}}
												>
													<Typography
														variant="caption"
														sx={{
															bgcolor: isSelected
																? "primary.main"
																: alpha(theme.palette.primary.main, 0.1),
															color: isSelected ? "#ffffff" : "primary.main",
															px: 0.6,
															py: 0.1,
															borderRadius: 0.5,
															fontWeight: 700,
															fontSize: "0.68rem",
															lineHeight: 1.2,
															whiteSpace: "nowrap",
														}}
													>
														{item.day}
													</Typography>
													<Typography
														variant="body2"
														sx={{
															fontWeight: 700,
															fontSize: { xs: "0.75rem", sm: "0.82rem" },
															color: isSelected
																? "primary.main"
																: "text.primary",
															lineHeight: 1.2,
															whiteSpace: "nowrap",
														}}
													>
														{item.label}
													</Typography>
												</Box>
											</Grid>
										);
									})}
								</Grid>
							) : (
								<Typography variant="caption" color="text.secondary">
									No fair dates available.
								</Typography>
							)}
							<input
								type="hidden"
								{...register("date", { required: true })}
								value={watchedDate}
							/>
						</Box>
					</Grid>

					{/* Ticket Quantity */}
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							size="small"
							label="Ticket Quantity"
							type="number"
							sx={{
								"& .MuiOutlinedInput-root": {
									p: 0,
									overflow: "hidden",
								},
								"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
								{
									WebkitAppearance: "none",
									margin: 0,
								},
								"& input[type=number]": {
									MozAppearance: "textfield",
								},
							}}
							inputProps={{
								min: 1,
								step: "1",
								style: {
									textAlign: "center",
									fontWeight: 700,
									fontSize: "1rem",
									padding: "8.5px 0",
									height: "20px",
								},
							}}
							{...register("quantity", {
								required: "Quantity is required",
								min: { value: 1, message: "Minimum quantity is 1" },
								valueAsNumber: true,
							})}
							error={Boolean(errors.quantity)}
							helperText={errors.quantity?.message}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleSubmit(onSubmit)();
								}
							}}
							InputProps={{
								startAdornment: (
									<IconButton
										type="button"
										onClick={handleDecrementQuantity}
										disabled={Number(watchedQuantity) <= 1}
										sx={{
											width: 38,
											height: 38,
											borderRadius: 0,
											borderRight: "1px solid",
											borderColor: "divider",
											bgcolor: alpha(theme.palette.primary.main, 0.05),
											color: "primary.main",
											"&:hover": {
												bgcolor: alpha(theme.palette.primary.main, 0.15),
											},
											"&.Mui-disabled": {
												bgcolor: "grey.100",
												color: "grey.400",
											},
										}}
									>
										<RemoveIcon sx={{ fontSize: 18 }} />
									</IconButton>
								),
								endAdornment: (
									<IconButton
										type="button"
										onClick={handleIncrementQuantity}
										sx={{
											width: 38,
											height: 38,
											borderRadius: 0,
											borderLeft: "1px solid",
											borderColor: "divider",
											bgcolor: alpha(theme.palette.primary.main, 0.05),
											color: "primary.main",
											"&:hover": {
												bgcolor: alpha(theme.palette.primary.main, 0.15),
											},
										}}
									>
										<AddIcon sx={{ fontSize: 18 }} />
									</IconButton>
								),
							}}
						/>
						{/* Compact Quick select buttons for 2 to 10 */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								mt: 1,
								flexWrap: "wrap",
							}}
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ fontWeight: 600, fontSize: "0.72rem", mr: 0.3 }}
							>
								Quick:
							</Typography>
							{QUICK_QUANTITIES.map((qty) => {
								const isSelected = Number(watchedQuantity) === qty;
								return (
									<Button
										key={qty}
										type="button"
										size="small"
										variant={isSelected ? "contained" : "outlined"}
										onClick={() =>
											setValue("quantity", qty, {
												shouldValidate: true,
												shouldDirty: true,
												shouldTouch: true,
											})
										}
										sx={{
											minWidth: 26,
											width: 28,
											height: 24,
											p: 0,
											fontSize: "0.75rem",
											fontWeight: 700,
											borderRadius: 1,
											borderColor: isSelected ? "primary.main" : "grey.300",
											color: isSelected ? "#fff" : "text.primary",
											bgcolor: isSelected ? "primary.main" : "background.paper",
											boxShadow: isSelected ? 1 : 0,
											"&:hover": {
												bgcolor: isSelected ? "primary.dark" : "action.hover",
												borderColor: isSelected
													? "primary.dark"
													: "primary.main",
											},
										}}
									>
										{qty}
									</Button>
								);
							})}
						</Box>
					</Grid>

					{/* Discount */}
					<Grid item xs={12} sm={6}>
						<TextField
							fullWidth
							size="small"
							label="Discount (BDT)"
							type="number"
							placeholder="0"
							inputProps={{ min: 0, step: "1" }}
							sx={{
								"& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
								{
									WebkitAppearance: "none",
									margin: 0,
								},
								"& input[type=number]": {
									MozAppearance: "textfield",
								},
							}}
							{...register("discount", {
								min: { value: 0, message: "Discount cannot be negative" },
							})}
							error={Boolean(errors.discount)}
							helperText={errors.discount?.message}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleSubmit(onSubmit)();
								}
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<Typography variant="caption" fontWeight={600}>
											BDT
										</Typography>
									</InputAdornment>
								),
							}}
						/>
					</Grid>

					{/* Payment Method */}
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth size="small">
							<InputLabel id="payment-method-label">Payment Method *</InputLabel>
							<Controller
								name="payment_method"
								control={control}
								rules={{ required: "Payment method is required" }}
								render={({ field }) => (
									<Select
										{...field}
										size="small"
										labelId="payment-method-label"
										label="Payment Method *"
									>
										{PAYMENT_METHODS.map((m) => (
											<MenuItem key={m.value} value={m.value}>
												{m.label}
											</MenuItem>
										))}
									</Select>
								)}
							/>
						</FormControl>
					</Grid>

					{/* Payment Status */}
					<Grid item xs={12} sm={6}>
						<FormControl fullWidth size="small">
							<InputLabel id="payment-status-label">Payment Status *</InputLabel>
							<Controller
								name="payment_status"
								control={control}
								rules={{ required: "Payment status is required" }}
								render={({ field }) => (
									<Select
										{...field}
										size="small"
										labelId="payment-status-label"
										label="Payment Status *"
									>
										{PAYMENT_STATUSES.map((s) => (
											<MenuItem key={s.value} value={s.value}>
												{s.label}
											</MenuItem>
										))}
									</Select>
								)}
							/>
						</FormControl>
					</Grid>

					{/* Calculation / Pricing Summary */}
					<Grid item xs={12}>
						<Box
							sx={{
								p: 1.5,
								borderRadius: 1.5,
								bgcolor: alpha(theme.palette.primary.main, 0.05),
								border: "1px solid",
								borderColor: alpha(theme.palette.primary.main, 0.15),
								display: "flex",
								flexDirection: { xs: "column", sm: "row" },
								justifyContent: "space-between",
								alignItems: { xs: "flex-start", sm: "center" },
								gap: 1,
							}}
						>
							<Box>
								<Typography variant="body2" color="text.secondary">
									Visit Date: <strong>{watchedDate}</strong> • Unit Price:{" "}
									<strong>৳{unitPrice}</strong> × {watchedQuantity} = ৳
									{grossTotal}
									{discountAmount > 0 && ` (Discount: -৳${discountAmount})`}
								</Typography>
							</Box>
							<Box>
								<Typography variant="h5" fontWeight={700} color="primary.main">
									Net Total: ৳{netPayable.toFixed(2)}
								</Typography>
							</Box>
						</Box>
					</Grid>

					{/* Action Buttons */}
					<Grid item xs={12} sx={{ mt: 1 }}>
						<Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
							<Button
								variant="outlined"
								color="inherit"
								size="small"
								disabled={isSubmitting}
								onClick={() => {
									setApiError("");
									setSuccessMsg("");
									reset({
										visitor_id: null,
										visitor_phone: "+880",
										visitor_name: "",
										visitor_email: "",
										password: "12345678",
										date: getDefaultTicketDate(ticketDates),
										quantity: 1,
										discount: 0,
										payment_method: "cash",
										payment_status: "paid",
									});
									setSelectedVisitor(null);
									setVisitorSearch("");
								}}
							>
								Reset
							</Button>

							<LoadingButton
								type="submit"
								variant="contained"
								color="primary"
								size="small"
								loading={isSubmitting}
								sx={{ px: 3, fontWeight: 700 }}
							>
								<span>Create Ticket</span>
							</LoadingButton>
						</Box>
					</Grid>
				</Grid>
			</Box>

			{/* Register New Visitor Modal (Accessible if needed) */}
			<CreateVisitor
				open={openCreateVisitorModal}
				handleClose={() => setOpenCreateVisitorModal(false)}
				onSuccessCallback={handleVisitorCreatedSuccess}
			/>
		</CommCard>
	);
};

export default CreateTicketPage;



{/* ========================================================================= */ }
{/* CURRENT SELECT VISITOR (COMMENTED OUT AS REQUESTED)                       */ }
{/* ========================================================================= */ }
{/*
					<Grid item xs={12}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 0.5,
							}}
						>
							<Typography variant="body2" fontWeight={600}>
								Select Visitor <span style={{ color: "red" }}>*</span>
							</Typography>
							<Button
								size="small"
								startIcon={<PersonAddAlt1Icon fontSize="small" />}
								onClick={() => setOpenCreateVisitorModal(true)}
								sx={{ textTransform: "none", fontWeight: 600, py: 0 }}
							>
								+ Register New Visitor
							</Button>
						</Box>

						<Controller
							name="visitor_id"
							control={control}
							rules={{ required: "Please select a visitor" }}
							render={({ field, fieldState: { error } }) => (
								<Autocomplete<IVisitorListItem, false, boolean, false>
									size="small"
									options={visitorsData?.data || []}
									loading={isVisitorsLoading}
									value={selectedVisitor}
									inputValue={visitorSearch}
									disableClearable
									isOptionEqualToValue={(option, value) => {
										if (typeof option === "string" || typeof value === "string") return false;
										return option?.visitor_id === value?.visitor_id;
									}}
									getOptionLabel={(option: IVisitorListItem | string) => {
										if (typeof option === "string") return option;
										if (!option) return "";
										return `${option.visitor_name || ""}${option.visitor_phone
											? ` (${option.visitor_phone})`
											: option.visitor_no
												? ` (${option.visitor_no})`
												: ""
											}`;
									}}
									renderOption={(props, option) => (
										<Box
											component="li"
											{...props}
											key={option.visitor_id}
											sx={{
												display: "flex !important",
												flexDirection: "column",
												alignItems: "flex-start !important",
												py: 0.75,
												px: 1.5,
												borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
												width: "100%",
											}}
										>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													width: "100%",
												}}
											>
												<Typography
													variant="body2"
													sx={{ fontWeight: 600, color: "text.primary" }}
												>
													{option.visitor_name}
												</Typography>
												{option.visitor_no && (
													<Typography
														variant="caption"
														sx={{
															bgcolor: "primary.main",
															color: "#ffffff",
															px: 0.7,
															py: 0.15,
															borderRadius: 0.5,
															fontWeight: 600,
															fontSize: "0.68rem",
														}}
													>
														{option.visitor_no}
													</Typography>
												)}
											</Box>
											{option.visitor_phone && (
												<Typography
													variant="caption"
													sx={{
														color: "text.secondary",
														fontSize: "0.75rem",
														mt: 0.25,
													}}
												>
													📱 {option.visitor_phone}
												</Typography>
											)}
										</Box>
									)}
									onInputChange={(_, value, reason) => {
										if (reason === "input") {
											setVisitorSearch(value);
										} else if (reason === "reset") {
											setVisitorSearch(value);
										} else if (reason === "clear") {
											setVisitorSearch("");
											setSelectedVisitor(null);
											field.onChange(null);
										}
									}}
									onChange={(_, value: IVisitorListItem | string | null) => {
										if (value && typeof value === "object") {
											setSelectedVisitor(value);
											field.onChange(value.visitor_id);
										} else {
											setSelectedVisitor(null);
											field.onChange(null);
											setVisitorSearch("");
										}
									}}
									renderInput={(params) => {
										const hasContent = Boolean(visitorSearch) || Boolean(selectedVisitor);
										return (
											<TextField
												{...params}
												size="small"
												placeholder="Search visitor by name, phone or email..."
												error={Boolean(error)}
												helperText={error?.message}
												InputProps={{
													...params.InputProps,
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon fontSize="small" color="action" />
														</InputAdornment>
													),
													endAdornment: (
														<Box
															sx={{
																position: "absolute",
																right: 8,
																top: "50%",
																transform: "translateY(-50%)",
																display: "flex",
																alignItems: "center",
																gap: 0.5,
															}}
														>
															{isVisitorsLoading && (
																<CircularProgress
																	color="primary"
																	size={16}
																/>
															)}
															{hasContent && (
																<IconButton
																	type="button"
																	aria-label="Clear visitor search"
																	size="small"
																	onMouseDown={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																	}}
																	onClick={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																		setVisitorSearch("");
																		setSelectedVisitor(null);
																		field.onChange(null);
																	}}
																	sx={{
																		p: 0.15,
																		color: "text.secondary",
																		"&:hover": {
																			color: "error.main",
																			bgcolor: "rgba(239, 68, 68, 0.1)",
																		},
																	}}
																>
																	<CloseIcon sx={{ fontSize: 17 }} />
																</IconButton>
															)}
															{params.InputProps.endAdornment}
														</Box>
													),
												}}
											/>
										);
									}}
								/>
							)}
						/>
					</Grid>
					*/}